import React, { useState } from 'react';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth }               from './context/AuthContext';
import { I18nProvider }                        from './context/I18nContext';
import { FeatureFlagProvider, useFeatureFlags } from './context/FeatureFlagContext';
import { FollowProvider }                      from './context/FollowContext';
import { IntegrationsProvider }                from './context/IntegrationsContext';
import { EmailProvider }                       from './context/EmailContext';
import { ContentProvider }                     from './context/ContentContext';

import Navbar         from './components/Navbar';
import AuthModal      from './components/AuthModal';
import FloatingCTA    from './components/FloatingCTA';
import Toast          from './components/Toast';
import Chatbot        from './components/Chatbot';
import MockModeBanner from './components/MockModeBanner';
import ProfileOnboardingModal, { isOnboardingNeeded } from './components/ProfileOnboardingModal';
import { getActiveReferral } from './utils/referral';

import Home          from './pages/Home';
import Mentors       from './pages/Mentors';
import Certifications from './pages/Certifications';
import BecomeMentor  from './pages/BecomeMentor';
import MentorProfile from './pages/MentorProfile';
import BookingPage   from './pages/BookingPage';
import EventsPage       from './pages/EventsPage';
import Training         from './pages/Training';
import TrainingArticle  from './pages/TrainingArticle';
import News             from './pages/News';
import BlogPage        from './pages/BlogPage';
import BlogArticle     from './pages/BlogArticle';
import TermsPage           from './pages/TermsPage';
import PrivacyPage         from './pages/PrivacyPage';
import ResetPasswordPage   from './pages/ResetPasswordPage';

import MentorDashboard      from './pages/dashboard/MentorDashboard';
import MenteeDashboard      from './pages/dashboard/MenteeDashboard';
import AdminDashboard       from './pages/dashboard/AdminDashboard';
import OAuthCallback        from './pages/OAuthCallback';
import MenteePublicProfile  from './pages/MenteePublicProfile';
import VerifyEmailPage      from './pages/VerifyEmailPage';

import './App.css';
import { warmupServer } from './utils/warmup';

export const AppContext = React.createContext();

function ProtectedRoute({ allowedRole, children }) {
  const { user, authReady } = useAuth();
  // ── Wait for session restore before deciding to redirect ──
  // Prevents blank screen / redirect flash during page reload
  if (!authReady) {
    return (
      <div style={{
        minHeight:'100vh', display:'flex', flexDirection:'column',
        alignItems:'center', justifyContent:'center', background:'#f9fafb', gap:16,
      }}>
        <div style={{
          width:48, height:48, border:'4px solid #e5e7eb',
          borderTopColor:'#1DB954', borderRadius:'50%',
          animation:'db-spin 0.7s linear infinite',
        }}/>
        <div style={{fontSize:'.9rem', color:'#9ca3af', fontWeight:600}}>Loading DevBrains…</div>
        <style>{`@keyframes db-spin{to{transform:rotate(360deg)}}`}</style>
      </div>
    );
  }
  if (!user) return <Navigate to="/" replace />;
  if (!user.isEmailVerified) return <Navigate to="/verify-email" replace />;
  if (allowedRole && user.role !== allowedRole) return <Navigate to={`/dashboard/${user.role}`} replace />;
  return children;
}

function AppInner() {
  // Track referral codes from URL ?ref=XXXX on every page load
  React.useEffect(() => { getActiveReferral(); warmupServer(); }, []);

  const [modalOpen,      setModalOpen]      = useState(false);
  const [onboardingOpen, setOnboardingOpen] = useState(false);
  const [toast,          setToast]          = useState(null);
  const [currency,       setCurrency]       = useState('INR');
  const location = useLocation();
  const { isEnabled } = useFeatureFlags();
  const { user } = useAuth();

  // Trigger onboarding once after login if profile info is missing
  React.useEffect(() => {
    if (user && isOnboardingNeeded(user.id)) {
      const t = setTimeout(() => setOnboardingOpen(true), 600);
      return () => clearTimeout(t);
    }
  }, [user?.id]); // eslint-disable-line

  const showToast = (icon, title, subtitle) => {
    setToast({ icon, title, subtitle });
    setTimeout(() => setToast(null), 5000);
  };

  const isDashboard = location.pathname.startsWith('/dashboard');

  return (
    <AppContext.Provider value={{ setModalOpen, showToast, currency, setCurrency }}>
      {!isDashboard && <Navbar onSignIn={() => setModalOpen(true)} />}

      <Routes>
        {/* ── Email verification ── */}
        <Route path="/verify-email" element={<VerifyEmailPage mode="pending" />} />
        <Route path="/verify"       element={<VerifyEmailPage mode="confirm" />} />

        {/* ── Public ── */}
        <Route path="/auth/callback"        element={<OAuthCallback />} />
        <Route path="/"                     element={<Home />} />
        <Route path="/mentors"              element={<Mentors />} />
        <Route path="/mentors/:slug"        element={<MentorProfile />} />
        <Route path="/mentors/:slug/book"   element={<BookingPage />} />
        <Route path="/mentee/:slug"         element={<MenteePublicProfile />} />
        <Route path="/become-mentor"        element={<BecomeMentor />} />
        <Route path="/training"             element={<Training onSignIn={() => setModalOpen(true)} />} />
        <Route path="/training/:slug"       element={<TrainingArticle onSignIn={() => setModalOpen(true)} />} />
        <Route path="/news"                 element={<News onSignIn={() => setModalOpen(true)} />} />
        <Route path="/blog"                element={<BlogPage onSignIn={() => setModalOpen(true)} />} />
        <Route path="/blog/article/:slug"  element={<BlogArticle onSignIn={() => setModalOpen(true)} />} />
        <Route path="/terms"               element={<TermsPage onSignIn={() => setModalOpen(true)} />} />
        <Route path="/privacy"             element={<PrivacyPage onSignIn={() => setModalOpen(true)} />} />
        <Route path="/reset-password"      element={<ResetPasswordPage />} />

        {/* ── Feature-gated public routes ── */}
        <Route path="/certifications"
          element={isEnabled('certifications') ? <Certifications /> : <Navigate to="/" replace />}
        />
        <Route path="/events"
          element={isEnabled('mentorEvents') ? <EventsPage /> : <Navigate to="/" replace />}
        />

        {/* ── Dashboards ── */}
        <Route path="/dashboard/mentor/*" element={
          <ProtectedRoute allowedRole="mentor"><MentorDashboard /></ProtectedRoute>
        } />
        <Route path="/dashboard/mentee/*" element={
          <ProtectedRoute allowedRole="mentee"><MenteeDashboard /></ProtectedRoute>
        } />
        <Route path="/dashboard/admin/*"  element={
          <ProtectedRoute allowedRole="admin"><AdminDashboard /></ProtectedRoute>
        } />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      {!isDashboard && (
        <>
          <AuthModal open={modalOpen} onClose={() => setModalOpen(false)} showToast={showToast} />
          <FloatingCTA onClick={() => setModalOpen(true)} />
          <Chatbot />
          <MockModeBanner />
        </>
      )}
      {toast && <Toast {...toast} />}
      <ProfileOnboardingModal
        open={onboardingOpen}
        onClose={() => setOnboardingOpen(false)}
      />
    </AppContext.Provider>
  );
}

export default function App() {
  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
      <ContentProvider>
      <I18nProvider>
        <FeatureFlagProvider>
          <EmailProvider>
          <IntegrationsProvider>
            <AuthProvider>
              <FollowProvider>
              <Router>
                <AppInner />
              </Router>
            </FollowProvider>
          </AuthProvider>
          </IntegrationsProvider>
          </EmailProvider>
        </FeatureFlagProvider>
      </I18nProvider>
      </ContentProvider>
    </GoogleOAuthProvider>
  );
}
